<script setup lang="ts">
import { computed } from "vue";
import CheckboxInput from "./Inputs/CheckboxInput.vue";
import type { IFooterActions } from "~/utils/interfaces/common-interfaces";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";

const props = defineProps({
  selectAll: {
    type: Boolean,
    default: true,
  },
  footerActions: {
    type: Array as PropType<IFooterActions[]>,
  },
  itemsName: {
    type: String,
  },
  modelValue: {
    type: Boolean,
  },
  excludedItemsCount: {
    type: Number,
  },
  selectedItemsCount: {
    type: Number,
  },
  totalCount: {
    type: Number,
  },
});

const emit = defineEmits(["update:modelValue", "reset"]);

// Under « Tout sélectionner » the selection is the whole list minus the unticked rows.
const selectedCount = computed(() =>
  props.modelValue && props.totalCount && !props.selectedItemsCount
    ? props.totalCount - (props.excludedItemsCount ?? 0)
    : (props.selectedItemsCount ?? 0),
);

function selectAll(val: any) {
  emit("update:modelValue", val);
}
</script>

<template #footer>
  <div class="justify-center flex flex-row gap-[16px] pointer-events-none">
    <label v-if="props.selectAll" class="box_style cursor-pointer">
      <CheckboxInput
        :checked="modelValue"
        :partial-select="(excludedItemsCount && modelValue) || (selectedItemsCount && !modelValue)"
        @update:model-value="selectAll($event)"
      ></CheckboxInput>
      <span class="body_med text-secondary-color whitespace-nowrap">Tout sélectionner</span>
    </label>

    <div class="box_style">
      <div class="flex flex-row gap-[8px]">
        <span
          v-if="modelValue && props.totalCount && !props?.selectedItemsCount"
          class="body_regular text-secondary-color"
          >{{ selectedCount }}/{{ props.totalCount }}</span
        >
        <span v-else class="body_med text-secondary-color">{{ selectedCount }}/{{ props.totalCount }}</span>
        <span class="body_regular text-secondary-color">{{ props.itemsName }}</span>
      </div>
      <div v-for="(item, i) in footerActions" :key="i" v-if="footerActions?.length">
        <Tooltip v-if="item.disabled && item.disabledTooltipText" position="top-mid" minified>
          <template #img>
            <button class="button_action_disabled" :disabled="true">
              <SvgIcon class="w-[16px] h-[16px]" :name="footerActions[i].logoName ?? 'message'"></SvgIcon>
              <span class="whitespace-nowrap">{{ item.name }}</span>
            </button>
          </template>
          <template #content>
            <div class="flex gap-[8px] items-center w-[300px]">
              <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
              <span class="body_regular">{{ item.disabledTooltipText }}</span>
            </div>
          </template>
        </Tooltip>
        <button
          v-else
          :class="
            item.disabled
              ? 'button_action_disabled'
              : item.cancelColor
                ? 'button_secondary_cancel_default'
                : 'button_action'
          "
          :disabled="item.disabled"
          @click="
            footerActions[i].action(selectedCount);
            emit('reset');
          "
        >
          <SvgIcon class="cursor-pointer w-[16px] h-[16px]" :name="footerActions[i].logoName ?? 'message'"></SvgIcon>
          <span class="whitespace-nowrap">{{ item.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "assets/scss/texts";

.box_style {
  @apply flex flex-row gap-[16px] items-center py-[12px] px-[16px] pointer-events-auto;
  background: var(--white-main);
  /* Blurred-shadow */
  box-shadow: var(--shadow-hard);
  border-radius: 16px;
}
.button_action {
  @apply body_regular flex flex-row gap-[4px] items-center;
  color: var(--primary-blue-color);

  &:hover {
    color: var(--blue-dark);
  }

  &:active {
    color: var(--blue-deep-dark);
  }
}
.button_action_disabled {
  @apply body_regular flex flex-row gap-[4px] items-center;
  color: var(--text-grey-medium-color);
}
</style>
